import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentsApi, Comment } from '@/lib/comments';
import { workspaceExtras, PersonRow } from '@/lib/workspace-extras';
import { useRealtimeInvalidate } from '@/hooks/useRealtimeInvalidate';

/**
 * Inline comment thread for any document / chunk / answer / conversation.
 *
 * Collaboration: anyone in the workspace can read and post. Authors show with
 * their real name + avatar. @mentions are chosen from a people picker (type
 * `@`) — the friendly name lands in the text while the exact user id is sent
 * alongside, so notifications resolve even when two people share a name.
 */
export function CommentThread({ workspaceId, targetKind, targetId }: {
  workspaceId: string;
  targetKind: string;
  targetId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', workspaceId, targetKind, targetId],
    queryFn: () => commentsApi.list(workspaceId, targetKind, targetId),
  });

  // New comments / @mentions push a `notification:new`; refetch the thread on
  // it instead of polling every 30s.
  useRealtimeInvalidate(['comments', workspaceId, targetKind, targetId]);

  const { data: people = [] } = useQuery({
    queryKey: ['people', workspaceId],
    queryFn: () => workspaceExtras.people(workspaceId),
    staleTime: 5 * 60_000,
  });

  // uuid → person, for resolving @mention chips on existing comments.
  const peopleById = useMemo(() => {
    const map = new Map<string, PersonRow>();
    for (const p of people) map.set(p.userId.toLowerCase(), p);
    return map;
  }, [people]);

  const matches = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    const pool = q
      ? people.filter(
          (p) =>
            (p.name ?? '').toLowerCase().includes(q) ||
            (p.email ?? '').toLowerCase().includes(q),
        )
      : people;
    return pool.slice(0, 6);
  }, [people, pickerQuery]);

  const create = useMutation({
    mutationFn: () =>
      commentsApi.create(workspaceId, { targetKind, targetId, body, mentions }),
    onSuccess: () => {
      setBody('');
      setMentions([]);
      setPickerOpen(false);
      qc.invalidateQueries({ queryKey: ['comments', workspaceId, targetKind, targetId] });
    },
  });

  const post = () => {
    if (!body.trim() || create.isPending) return;
    create.mutate();
  };

  // Detect a trailing `@query` at the cursor and open the picker.
  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setBody(val);
    const pos = e.target.selectionStart ?? val.length;
    const before = val.slice(0, pos);
    const m = before.match(/@([\p{L}\p{N}_.-]*)$/u);
    if (m) {
      setPickerQuery(m[1]);
      setPickerOpen(true);
      setHighlight(0);
    } else {
      setPickerOpen(false);
    }
  };

  const pick = (person: PersonRow) => {
    const ta = textareaRef.current;
    const pos = ta?.selectionStart ?? body.length;
    const label = displayName(person);
    const before = body.slice(0, pos).replace(/@([\p{L}\p{N}_.-]*)$/u, `@${label} `);
    const after = body.slice(pos);
    setBody(before + after);
    setMentions((prev) => (prev.includes(person.userId) ? prev : [...prev, person.userId]));
    setPickerOpen(false);
    setPickerQuery('');
    requestAnimationFrame(() => ta?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (pickerOpen && matches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[highlight]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setPickerOpen(false); return; }
    }
    // Enter posts; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      post();
    }
  };

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {comments.length === 0 && (
          <li className="text-xs text-ink-500">
            {t('comments.empty', { defaultValue: 'No comments yet — start the discussion.' })}
          </li>
        )}
        {comments.map((c) => (
          <CommentItem key={c.id} comment={c} peopleById={peopleById} t={t} />
        ))}
      </ul>

      <div className="relative">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={t('comments.placeholder', { defaultValue: 'Add a comment. Type @ to mention a teammate.' }) as string}
            rows={2}
            className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          <button
            onClick={post}
            disabled={create.isPending || !body.trim()}
            className="self-start px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {create.isPending
              ? t('comments.posting', { defaultValue: 'Posting…' })
              : t('comments.post', { defaultValue: 'Post' })}
          </button>
        </div>

        {pickerOpen && matches.length > 0 && (
          <ul className="absolute z-20 left-0 bottom-full mb-1 w-72 max-h-56 overflow-auto rounded-lg border border-ink-200 bg-white shadow-card py-1">
            {matches.map((p, i) => (
              <li key={p.userId}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                  className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm ${
                    i === highlight ? 'bg-indigo-50' : 'hover:bg-ink-50'
                  }`}
                >
                  <Avatar person={p} />
                  <span className="min-w-0">
                    <span className="block truncate text-ink-900">{displayName(p)}</span>
                    {p.email && <span className="block truncate text-[11px] text-ink-500">{p.email}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {mentions.length > 0 && (
        <p className="text-[11px] text-ink-500">
          {t('comments.will_notify', { defaultValue: 'Will notify:' })}{' '}
          {mentions
            .map((id) => {
              const p = peopleById.get(id.toLowerCase());
              return p ? displayName(p) : id.slice(0, 8);
            })
            .join(', ')}
        </p>
      )}
    </div>
  );
}

function CommentItem({
  comment,
  peopleById,
  t,
}: {
  comment: Comment;
  peopleById: Map<string, PersonRow>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const name =
    comment.author?.name ||
    comment.author?.email ||
    (comment.userId ? `${comment.userId.slice(0, 8)}…` : t('comments.unknown_user', { defaultValue: 'Someone' }));
  const mentionNames = comment.mentions
    .map((id) => peopleById.get(id.toLowerCase()))
    .filter((p): p is PersonRow => !!p)
    .map((p) => displayName(p));

  return (
    <li className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 mb-1">
        <Avatar
          person={{
            name: comment.author?.name ?? null,
            email: comment.author?.email ?? null,
            pictureUrl: comment.author?.pictureUrl ?? null,
          }}
        />
        <span className="text-[13px] font-medium text-ink-900">{name}</span>
        <span className="text-[11px] text-ink-400 num">{new Date(comment.createdAt).toLocaleString()}</span>
      </div>
      <p className="text-ink-800 leading-relaxed whitespace-pre-wrap">{comment.body}</p>
      {mentionNames.length > 0 && (
        <p className="mt-1 text-[11px] text-indigo-700">
          {mentionNames.map((n) => `@${n}`).join(' ')}
        </p>
      )}
    </li>
  );
}

function Avatar({ person }: { person: Pick<PersonRow, 'name' | 'email' | 'pictureUrl'> }) {
  if (person.pictureUrl) {
    return <img src={person.pictureUrl} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />;
  }
  const initials = displayName(person).slice(0, 2).toUpperCase();
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white text-[10px] font-semibold">
      {initials}
    </span>
  );
}

function displayName(p: Pick<PersonRow, 'name' | 'email'>): string {
  if (p.name && p.name.trim()) return p.name.trim();
  if (p.email) return p.email.split('@')[0];
  return 'user';
}
